// src/components/pos/SalesHistory.jsx
// POS Sales History Component - Shows completed and paid orders with date filtering

import { useState, useEffect, useCallback } from 'react'
import { ShoppingCart, X, Calendar, Filter, Eye, Receipt, Search, Printer } from 'lucide-react'
import { cn } from '../../utils/cn'
import { POSButton } from './ui/Button'
import OrderService from '../../services/orderService'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { formatUTCDate } from '../../utils/dateUtils'
import { HTMLReceiptGenerator } from '../../utils/htmlReceiptGenerator'

const SalesHistory = ({ className, branchId, ...props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)

  // Get today's date
  const getTodayDate = () => {
    const today = new Date()
    return format(today, 'yyyy-MM-dd')
  }

  const fetchOrders = useCallback(async () => {
    if (!branchId) {
      toast.error('Branch ID is required')
      return
    }

    setLoading(true)
    try {
      const todayDate = getTodayDate()
      // Get tomorrow's date for the range (to include all of today's orders)
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const tomorrowDate = format(tomorrow, 'yyyy-MM-dd')
      
      const params = {
        page: 1,
        limit: 50,
        branch: branchId,
        orderStatus: 'completed',
        paymentStatus: 'paid',
        dateFrom: todayDate,
        dateTo: tomorrowDate,
        sortBy: 'orderDate',
        sortOrder: 'desc'
      }

      console.log('📋 Fetching sales history with params:', params)
      const response = await OrderService.getAllOrders(params)
      
      if (response?.data?.orders) {
        console.log('✅ Sales history raw response:', response.data)
        console.log('📦 First order structure:', response.data.orders[0])
        setOrders(response.data.orders)
        console.log('✅ Sales history fetched:', response.data.orders.length, 'orders')
      } else {
        setOrders([])
        console.log('⚠️ No orders found in response')
      }
    } catch (error) {
      console.error('❌ Error fetching sales history:', error)
      toast.error('Failed to fetch sales history')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [branchId])

  // Fetch orders when modal opens
  useEffect(() => {
    if (isOpen && branchId) {
      fetchOrders()
    }
  }, [isOpen, branchId, fetchOrders])

  const handleOrderView = (order) => {
    setSelectedOrder(order)
    setShowOrderDetails(true)
  }

  // Print receipt handler
  const handlePrintReceipt = async (order) => {
    try {
      console.log('🖨️ SalesHistory - Printing receipt for order:', order.orderNumber)
      console.log('🖨️ SalesHistory - Order data:', order)
      console.log('🖨️ SalesHistory - Order items:', order.orderItems)
      console.log('🖨️ SalesHistory - Order payment:', order.orderPayment)
      
      // Get branch info from local storage if available
      const user = JSON.parse(localStorage.getItem('tezlaa-user') || '{}')
      const branchName = user.branch?.name || 'Tezlaa Cafe'
      
      // Prepare receipt options
      const receiptOptions = {
        companyName: branchName,
        companyAddress: user.branch?.address || 'Your Business Address',
        companyPhone: user.branch?.phoneNumber || 'Your Phone Number',
        companyEmail: user.branch?.email || 'contact@tezlaa.com'
      }
      
      // Print receipt using HTMLReceiptGenerator
      HTMLReceiptGenerator.printReceipt(order, receiptOptions)
      
      toast.success('Receipt sent to printer')
    } catch (error) {
      console.error('❌ Error printing receipt:', error)
      toast.error('Failed to print receipt')
    }
  }

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    return (
      order.orderNumber?.toLowerCase().includes(searchLower) ||
      order.customer?.name?.toLowerCase().includes(searchLower) ||
      order.customer?.email?.toLowerCase().includes(searchLower) ||
      order.customer?.mobileNumber?.toLowerCase().includes(searchLower) ||
      order.customer?.phone?.toLowerCase().includes(searchLower)
    )
  })

  const getTotalSales = () => {
    return filteredOrders.reduce((total, order) => {
      const orderTotal = order.orderPayment?.totalAmount || 0
      return total + orderTotal
    }, 0)
  }

  const formatOrderType = (orderType) => {
    switch (orderType) {
      case 'dine_in':
        return 'Dine-In'
      case 'take_away':
        return 'Takeaway'
      case 'delivery':
        return 'Delivery'
      default:
        return orderType?.replace('_', ' ').toUpperCase() || 'Unknown'
    }
  }

  const formatDateTime = (dateString) => {
    return formatUTCDate(dateString, 'MMM dd, yyyy HH:mm')
  }

  return (
    <>
      {/* Trigger Button */}
      <POSButton
        variant="default"
        className={cn(
          "h-20 flex-col space-y-2 bg-gray-900 text-white hover:bg-gray-600 border-gray-700",
          className
        )}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        <ShoppingCart className="h-6 w-6" />
        <span className="text-sm font-medium">Sales</span>
      </POSButton>

      {/* Sales History Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center" style={{ zIndex: 100000 }}>
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-full mx-4 h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-500 to-green-600">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Sales History</h3>
                  <p className="text-sm text-green-100">Completed and paid orders</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Filters */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-center gap-4">
                {/* Today's Date Display */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-semibold text-gray-700">Today's Sales:</span>
                  <span className="text-sm font-medium text-green-600">{format(new Date(), 'MMMM dd, yyyy')}</span>
                </div>

                {/* Search */}
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <Search className="h-4 w-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by order number, customer..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Refresh Button */}
                <button
                  onClick={fetchOrders}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {/* Summary */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {filteredOrders.length} orders found today
                </div>
                <div className="text-lg font-bold text-green-600">
                  Total Sales: Rs.{getTotalSales().toFixed(2)}
                </div>
              </div>
            </div>

            {/* Orders List */}
            <div className="flex-1 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-lg text-gray-500">Loading orders...</div>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Receipt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <div className="text-lg text-gray-500 mb-2">No orders found</div>
                    <div className="text-sm text-gray-400">
                      {searchTerm ? (
                        <>Try a different search term</>
                      ) : (
                        <>No orders found for today</>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Discount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredOrders.map((order) => (
                        <tr key={order._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {order.orderNumber}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDateTime(order.orderDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {order.customer?.name || 'Walking Customer'}
                            </div>
                            {(order.customer?.mobileNumber || order.customer?.phone) && (
                              <div className="text-xs text-gray-500">
                                {order.customer.mobileNumber || order.customer.phone}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {formatOrderType(order.orderType)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {order.orderItems?.length || 0} items
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(() => {
                                const discount = order.orderPromotionEngineResult?.orderDetails?.totalDiscount || 0
                                return discount > 0 ? (
                                  <span className="text-green-600 font-medium">
                                    - Rs.{discount.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              Rs.{(order.orderPayment?.totalAmount || 0).toFixed(2)}
                            </div>
                            {order.orderPromotionEngineResult?.hasPromotion && (
                              <div className="text-xs text-green-600">
                                {order.orderPromotionEngineResult.orderDetails.discountPercentage}% off
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {order.orderPayment?.paymentMethods?.[0]?.method?.toUpperCase() || 'PAID'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handlePrintReceipt(order)}
                                className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                title="Print Receipt"
                              >
                                <Printer className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleOrderView(order)}
                                className="text-green-600 hover:text-green-900 flex items-center gap-1"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center" style={{ zIndex: 100001 }}>
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowOrderDetails(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600">
              <div>
                <h3 className="text-xl font-semibold text-white">Order Details</h3>
                <p className="text-sm text-blue-100">{selectedOrder.orderNumber}</p>
              </div>
              <button
                onClick={() => setShowOrderDetails(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Order Details Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Order Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-gray-500">Date:</span> {formatDateTime(selectedOrder.orderDate)}</div>
                    <div><span className="text-gray-500">Type:</span> {formatOrderType(selectedOrder.orderType)}</div>
                    <div><span className="text-gray-500">Status:</span> {selectedOrder.orderStatus}</div>
                    {selectedOrder.table && (
                      <div><span className="text-gray-500">Table:</span> {selectedOrder.table.name || selectedOrder.table.number}</div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-gray-500">Name:</span> {selectedOrder.customer?.name || 'Walking Customer'}</div>
                    {(selectedOrder.customer?.mobileNumber || selectedOrder.customer?.phone) && (
                      <div><span className="text-gray-500">Phone:</span> {selectedOrder.customer.mobileNumber || selectedOrder.customer.phone}</div>
                    )}
                    {selectedOrder.customer?.email && (
                      <div><span className="text-gray-500">Email:</span> {selectedOrder.customer.email}</div>
                    )}
                    {selectedOrder.customer?.pointsBalance !== undefined && (
                      <div><span className="text-gray-500">Points Balance:</span> {selectedOrder.customer.pointsBalance}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Order Items</h4>
                <div className="space-y-2">
                  {selectedOrder.orderItems?.map((item, index) => (
                    <div key={item._id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{item.product?.name}</div>
                        <div className="text-sm text-gray-500">
                          Rs.{(item.unitPrice || 0).toFixed(2)} x {item.quantity}
                        </div>
                        {item.promotionEngineResult?.hasPromotion && (
                          <div className="text-xs text-green-600">
                            {item.promotionEngineResult.promotion.name} - {item.promotionEngineResult.totals.discountPercentage}% off
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {item.promotionEngineResult?.hasPromotion ? (
                          <div>
                            <div className="text-sm text-gray-500 line-through">
                              Rs.{(item.promotionEngineResult.totals.originalPrice || 0).toFixed(2)}
                            </div>
                            <div className="font-medium text-green-600">
                              Rs.{(item.promotionEngineResult.totals.finalPrice || 0).toFixed(2)}
                            </div>
                          </div>
                        ) : (
                          <div className="font-medium">
                            Rs.{((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Promotion Details */}
              {selectedOrder.orderPromotionEngineResult?.hasPromotion && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Order Promotion Applied</h4>
                  <div className="text-sm text-green-700">
                    <div><strong>{selectedOrder.orderPromotionEngineResult.promotion.name}</strong></div>
                    <div>{selectedOrder.orderPromotionEngineResult.promotion.description}</div>
                    <div className="mt-2">
                      <span className="text-green-600">
                        {selectedOrder.orderPromotionEngineResult.calculationDetails?.calculationMethod}
                      </span>
                    </div>
                    <div className="mt-1 text-green-800 font-medium">
                      Total Savings: Rs.{(selectedOrder.orderPromotionEngineResult.orderDetails.totalSavings || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details */}
              {selectedOrder.orderPayment && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-2">Payment Information</h4>
                  <div className="space-y-2">
                    {selectedOrder.orderPayment.paymentMethods?.map((payment, index) => (
                      <div key={payment._id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium capitalize">{payment.method}</div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(payment.createdAt)}
                          </div>
                          {payment.cashDetails && (
                            <div className="text-xs text-gray-500">
                              Received: Rs.{payment.cashDetails.receivedAmount.toFixed(2)} | 
                              Change: Rs.{payment.cashDetails.changeAmount.toFixed(2)}
                            </div>
                          )}
                        </div>
                        <div className="font-medium">
                          Rs.{(payment.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Original Amount:</span>
                    <span>Rs.{(selectedOrder.orderPromotionEngineResult?.orderDetails?.originalOrderValue || 0).toFixed(2)}</span>
                  </div>
                  
                  {selectedOrder.orderPromotionEngineResult?.orderDetails?.totalDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Total Discount ({selectedOrder.orderPromotionEngineResult.orderDetails.discountPercentage}%):</span>
                      <span>-Rs.{(selectedOrder.orderPromotionEngineResult.orderDetails.totalDiscount || 0).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span>Subtotal (After Promotions):</span>
                    <span>Rs.{(selectedOrder.fees?.baseAmountAfterPromotions || 0).toFixed(2)}</span>
                  </div>

                  {selectedOrder.fees?.serviceFeeAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Service Fee ({selectedOrder.fees.serviceFeeRate}%):</span>
                      <span>Rs.{(selectedOrder.fees.serviceFeeAmount || 0).toFixed(2)}</span>
                    </div>
                  )}

                  {selectedOrder.fees?.packageFeeAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Package Fee ({selectedOrder.fees.packageFeeRate}%):</span>
                      <span>Rs.{(selectedOrder.fees.packageFeeAmount || 0).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Final Total:</span>
                    <span>Rs.{(selectedOrder.orderPayment?.totalAmount || 0).toFixed(2)}</span>
                  </div>

                  {selectedOrder.orderPromotionEngineResult?.orderDetails?.totalSavings > 0 && (
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>You Saved:</span>
                      <span>Rs.{(selectedOrder.orderPromotionEngineResult.orderDetails.totalSavings || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SalesHistory
